import { useEffect } from "react";
import { Router, RouteComponentProps, RouterProps } from "@reach/router";
import { makeStyles } from "@material-ui/core/styles";
import { ErrorHandler } from "@ploy-lib/core";
import {
	ErrorPage,
	NoMatch,
	ImageFit,
	ThirdPartyScripts,
	SignedOnUser,
	GlobalSuspense
} from "@ploy-ui/core";
import { Theme } from "@material-ui/core";

import { ApplicationView, ProductView } from "@ploy-ui/form-views";
import { useResource, useInvalidator } from "@rest-hooks/core";
import {
	PartnerWebShortRefResource,
	LoginResource
} from "@ploy-lib/rest-resources";
import { useTenantImage, useTenant } from "@ploy-ui/tenants";
import { BootstrapQueryEvents } from "./AuthenticatedAgent";
import { SnackbarProvider } from "notistack";
import clsx from "clsx";
import { Helmet } from "react-helmet";

const useStyles = makeStyles(
	(theme: Theme) => ({
		root: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			flexDirection: "column"
		},
		page: {
			maxWidth: 1440,
			width: "100%"
		},
		logoWrapper: {
			paddingTop: theme.spacing(4),
			paddingBottom: theme.spacing(1),
			[theme.breakpoints.down("sm")]: {
				paddingTop: theme.spacing(1)
			}
		},
		logo: {
			width: "100%",
			height: 64
		},
		signedIn: {
			paddingRight: theme.spacing(4)
		}
	}),
	{ name: "DployFormViews" }
);
// Disallow the field role navigate_home as it doesn'ẗ make sense in snippet context
const disallowedFieldRoles = ["navigate_home"] as const;

function ApplicationByShortRef({
	"*": _,
	shortRef,
	...props
}: RouteComponentProps<{ "*": string; shortRef: string }> & RouterProps) {
	const { applicationNumber } = useResource(
		PartnerWebShortRefResource.detail(),
		{ shortRef }
	);

	const loginStatus = useInvalidator(LoginResource.status());

	useEffect(() => {
		loginStatus({});
	}, [loginStatus, shortRef]);

	return (
		<ApplicationView
			{...props}
			applicationNumber={applicationNumber!}
			disallowedFieldRoles={disallowedFieldRoles}
		/>
	);
}

export const App = ({
	"*": _,
	...props
}: RouteComponentProps<{ "*": string }> & RouterProps) => {
	const classes = useStyles(props);

	const logo = useTenantImage("form_web_logo");

	return (
		<>
			<Helmet htmlAttributes={{ lang: useTenant().locale, translate: "no" }} />
			<SnackbarProvider maxSnack={3}>
				<GlobalSuspense>
					<div className={clsx(classes.logoWrapper, classes.root)}>
						{logo ? (
							<ImageFit
								className={classes.logo}
								src={logo}
								alt="logo"
								fit="contain"
							/>
						) : null}
					</div>
					<SignedOnUser align="right" className={classes.signedIn} />
					<div className={classes.root}>
						<Router {...props} className={classes.page}>
							<ErrorHandler<RouteComponentProps>
								path="/"
								fallback={error => (
									<Router>
										<ErrorPage default error={error} />
									</Router>
								)}
							>
								<ApplicationByShortRef path="ref/:shortRef/*" />

								<ApplicationView
									path="application/:applicationNumber/*"
									disallowedFieldRoles={disallowedFieldRoles}
								/>

								<BootstrapQueryEvents default>
									<ProductView
										key="product"
										path="product/:externalCode/*"
										disallowedFieldRoles={disallowedFieldRoles}
									/>

									<ProductView
										key="product"
										path="calculator/:externalCode/*"
										enableSelect
										formContext="Calculator"
										disallowedFieldRoles={disallowedFieldRoles}
									/>
									<ProductView
										key="product"
										path="calculator"
										enableSelect
										formContext="Calculator"
										disallowedFieldRoles={disallowedFieldRoles}
									/>

									<ProductView
										key="product"
										path="all/:externalCode/*"
										enableSelect
										disallowedFieldRoles={disallowedFieldRoles}
									/>
									<ProductView
										key="product"
										path="product"
										enableSelect
										disallowedFieldRoles={disallowedFieldRoles}
									/>
									<ProductView
										key="product"
										path=":category/:externalCode/*"
										enableSelect
										disallowedFieldRoles={disallowedFieldRoles}
									/>
									<ProductView
										key="product"
										path=":category"
										enableSelect
										disallowedFieldRoles={disallowedFieldRoles}
									/>

									<NoMatch default noThrow to="all" />
								</BootstrapQueryEvents>
							</ErrorHandler>
						</Router>
						<ThirdPartyScripts />
					</div>
				</GlobalSuspense>
			</SnackbarProvider>
		</>
	);
};
